/**
 * 登录页的「方式 / 面板」规则，全是纯函数。
 *
 * 为什么从 `LoginView.vue` 里抽出来：`apps/web/vitest.config.ts` 只收 `src` 下的 `.test.ts`、
 * `environment` 是 node，仓库里没有任何 .vue 组件测试。登录页的三种形态（手机号 / 邮箱 /
 * 管理员口令）以及各自的副标题如果写死在模板里的三元表达式里，就只能靠手点验证；
 * 抽成纯函数后，"哪个面板该显示哪句话""这段输入算不算 6 位验证码"都能被单测钉住。
 *
 * 这里的每个函数都不碰 Vue、不碰网络，只做字符串与分支。
 */

/** 顶部 tablist 的两个 tab。原来的「管理员」tab 已下移到「其他方式」，不再是 tab。 */
export type LoginMode = 'sms' | 'email';

/**
 * 当前面板：两个 tab，外加展开后的管理员口令表单。
 *
 * 它与 `LoginMode` 分开，是因为口令表单**不属于任何 tab**——它挂在页面底部，
 * 展开时 tab 还在原处。用同一个联合类型表达会让人以为"口令是一个 tab"。
 */
export type LoginPanel = LoginMode | 'staff';

export interface LoginModeTab {
  readonly value: LoginMode;
  readonly label: string;
}

/** tablist 的顺序即展示顺序：手机号在前（存量用户的主通道），邮箱在后。 */
export const LOGIN_MODE_TABS: ReadonlyArray<LoginModeTab> = [
  { value: 'sms', label: '手机号验证码' },
  { value: 'email', label: '邮箱验证码' },
];

/**
 * ★ test_server 分支临时开关：手机号验证码登录已停用。
 *
 * 与 `apps/server/src/auth/auth.controller.ts` 里的 `SMS_LOGIN_ENABLED` 是同一件事的两半：
 * 服务端直接以 50300 拒绝 `/auth/sms/*`，这里则让登录页不再把用户送到一个必然失败的表单前
 * （tab 仍然保留，点了会看到下面那句说明——按这个页面的既有口径，入口不靠"藏起来"挡人）。
 * 恢复时把两处一起改回 `true` 即可。
 */
export const SMS_LOGIN_ENABLED: boolean = false;

/** 手机号通道被停用时，sms 面板顶上那句话。 */
export const SMS_LOGIN_DISABLED_NOTICE =
  '手机号验证码登录已在测试环境临时关闭，请改用邮箱验证码；管理员可在下方「其他方式」里用账号密码登录。';

/**
 * 当前该讲哪一件事。
 *
 * 管理员口令面板一展开就压过 tab：它是从「其他方式」里点开的，用户眼前此刻是账号密码
 * 表单，副标题还写着"邮箱验证码"会让人以为填错了地方。
 */
export function activeLoginPanel(mode: LoginMode, staffExpanded: boolean): LoginPanel {
  return staffExpanded ? 'staff' : mode;
}

/**
 * 副标题：三种面板各一句，说清「这个通道会自动建号吗、谁可以用」。
 *
 * 邮箱那句要写出有效期：验证码是 5 分钟（契约 `EmailExpireSeconds = 300`），
 * 而手机号那句不写，因为原来就没有——顺手改掉会让这次改动的范围失控。
 */
export function loginPanelSubtitle(panel: LoginPanel): string {
  if (panel === 'email') {
    return '未注册的邮箱将自动创建账号。验证码 5 分钟内有效，登录后会话历史与额度会保留在你的账号下。';
  }
  if (panel === 'staff') {
    return '管理员与 ROOT 使用账号密码登录；普通用户请改用手机号或邮箱验证码。';
  }
  if (!SMS_LOGIN_ENABLED) {
    return SMS_LOGIN_DISABLED_NOTICE;
  }
  return '未注册的手机号将自动创建账号。登录后会话历史与额度会保留在你的账号下。';
}

/**
 * 验证码输入框的清洗：只留数字，并截断到 6 位。
 *
 * 在输入阶段就剥掉非数字，粘贴「123 456」也能用——`SixDigitCodeSchema` 只认连续的 6 位数字，
 * 把这步留到提交时再报"格式不对"，用户看着一个空格回不到原因。
 */
export function sanitizeCodeInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 6);
}
